import { useId, type SVGProps } from "react";

export type LogoTone = "auto" | "light" | "dark";

export type LogoProps = SVGProps<SVGSVGElement> & {
  /** "auto" (default) traces currentColor — pair with a `text-fg-1` parent.
   *  "dark" forces ink fill; "light" forces paper. */
  tone?: LogoTone;
  /** Pixel size shorthand. Width = size, height respects the asset ratio. */
  size?: number;
  title?: string;
};

const inkFill = "#0E0E0C";
const paperFill = "#FCFCFB";

// The mark is monochrome — a single fill carries the whole glyph. "auto"
// inherits the surface text color so the logo follows the active theme.
function markFill(tone: LogoTone) {
  if (tone === "dark") return inkFill;
  if (tone === "light") return paperFill;
  return "currentColor";
}

// Mark — the gate glyph alone. 64×64 native; scale via `size`.
//
// Shared brand mark with `apps/website` (`public/logo-mark*.svg`): a portal
// disc, opened by a slot, ringed by the approach dots of an inbound scan.
export function LogoMark({
  tone = "auto",
  size = 28,
  title,
  className = "",
  ...rest
}: LogoProps) {
  const fill = markFill(tone);
  const maskId = useId();
  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      fill="none"
      role={title ? "img" : "presentation"}
      aria-hidden={title ? undefined : true}
      className={className}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <mask id={maskId} maskUnits="userSpaceOnUse">
        <rect width="64" height="64" fill="white" />
        <circle cx="32" cy="32" r="14" fill="black" />
        <path d="M 32 32 L 64.68 22.63 L 64.68 41.37 Z" fill="black" />
        <circle cx="23.3475" cy="5.3704" r="2.4" fill="black" />
        <circle cx="9.3475" cy="15.5420" r="2.4" fill="black" />
        <circle cx="4.0000" cy="32.0000" r="2.4" fill="black" />
        <circle cx="9.3475" cy="48.4580" r="2.4" fill="black" />
        <circle cx="23.3475" cy="58.6296" r="2.4" fill="black" />
      </mask>
      <circle cx="32" cy="32" r="28" fill={fill} mask={`url(#${maskId})`} />
      <rect x="32" y="30.5" width="24" height="3" fill={fill} />
    </svg>
  );
}

